/**
 * Which aeroplanes exist yet.
 *
 * The scout dates are the game's own, read out of `FIGHTER.DAT`: 22 records of
 * 9 bytes with a 0-based intro month and a two-digit intro year (record layout
 * and verification in docs/formats/missions-campaign.md § *`FIGHTER.DAT`*).
 * Decoded here against `data/original/RedBaron/SIERRA/REDBARON/FIGHTER.DAT`
 * (497 bytes, the 22-record revision), which agrees with the manual everywhere
 * the manual gives a date and supplies the five the manual omits. Where the two
 * disagree the file wins: the Morane Bullet is November 1915 in `FIGHTER.DAT`
 * against the manual's April 1915, and it is the file that drives the game.
 *
 * The eleven two-seaters and bombers are not in `FIGHTER.DAT` — it is the
 * *flyable* roster — so their dates come from docs/game/aircraft.md §10's
 * manual-sourced table instead, and are marked as such in `INTRODUCTION_SOURCE`.
 *
 * This is what makes a career's sky change: `AIRCRAFT INTRODUCTION` — *"The
 * [nation] has begun deploying a new aircraft to its fighter units today. The
 * new scout is called the [plane]"* (`PS.EXE`) — fires when the calendar
 * crosses one of these months, and the squadron re-equips onto what has just
 * arrived.
 */

import { type AircraftKey } from "../flight/aircraft-table.ts";
import { SCOUT_KEYS, aircraftLabel, nationOf, type Nation } from "../mission/mission-roster.ts";
import { monthIndexOf, type CareerDate } from "./calendar.ts";
import { SERVICE_LABELS, serviceFor } from "./service.ts";

/** Which document each date came from, so a reader can tell a file read from a manual read. */
export const INTRODUCTION_SOURCE = {
  scouts: "FIGHTER.DAT (22-record revision), decoded per docs/formats/missions-campaign.md",
  twoSeaters: "docs/game/aircraft.md §10, manual introduction dates (these types are not in FIGHTER.DAT)",
} as const;

const intro = monthIndexOf;

/** Career month index each type enters service. Negative means "before the calendar starts". */
export const AIRCRAFT_INTRODUCTIONS: Readonly<Record<AircraftKey, number>> = {
  MORANE: intro(1915, 10),
  FOKE3: intro(1915, 10),
  NIEU11: intro(1915, 5),
  AIRCO: intro(1916, 1),
  NIEUPORT: intro(1916, 3),
  SOPPUP: intro(1916, 3),
  HALBD2: intro(1916, 4),
  SPAD_7: intro(1916, 6),
  ALBA_D2: intro(1916, 8),
  SOPTRI: intro(1917, 0),
  ALBA_D3: intro(1917, 1),
  SE5: intro(1917, 4),
  ALBA_D5: intro(1917, 5),
  CAMEL: intro(1917, 5),
  PFALZ: intro(1917, 6),
  FOKDR1: intro(1917, 7),
  SPAD_13: intro(1917, 8),
  NIEU28: intro(1918, 0),
  FOKD7: intro(1918, 4),
  SIEMENS: intro(1918, 6),
  FOKD8: intro(1918, 7),
  SNIPE: intro(1918, 8),
  BE2: intro(1914, 7),
  AVIATIK: intro(1915, 0),
  FE2: intro(1915, 9),
  ROLAND: intro(1916, 3),
  RE8: intro(1916, 9),
  HANDLEY: intro(1916, 10),
  GOTHA: intro(1917, 1),
  RUMPLER: intro(1917, 2),
  BRISTOL: intro(1917, 3),
  DH4: intro(1917, 4),
  JUNKER: intro(1917, 10),
};

export function introductionMonth(key: AircraftKey): number {
  return AIRCRAFT_INTRODUCTIONS[key];
}

export function isInService(key: AircraftKey, date: CareerDate): boolean {
  return date.month >= introductionMonth(key);
}

/** Every scout this side has in service on this date, oldest first. */
export function scoutsInService(nation: Nation, date: CareerDate): readonly AircraftKey[] {
  return SCOUT_KEYS.filter((key) => nationOf(key) === nation && isInService(key, date)).sort(
    (left, right) => introductionMonth(left) - introductionMonth(right),
  );
}

/** How many of the newest types a squadron is realistically flying at once. */
export const FRONT_LINE_TYPES = 3;

/**
 * What a squadron is actually equipped with: the newest types in service, not
 * the whole back catalogue. A 1918 Jasta is not still being issued Eindeckers.
 */
export function frontLineScouts(nation: Nation, date: CareerDate, count = FRONT_LINE_TYPES): readonly AircraftKey[] {
  const inService = scoutsInService(nation, date);
  return inService.slice(Math.max(0, inService.length - count));
}

/** Types that entered service after `from` and are in service by `to`. */
export function introducedBetween(nation: Nation, from: CareerDate, to: CareerDate): readonly AircraftKey[] {
  return scoutsInService(nation, to).filter((key) => introductionMonth(key) > from.month);
}

/** Verbatim `PS.EXE` AIRCRAFT INTRODUCTION wording, with the service named. */
export function introductionMessage(nation: Nation, date: CareerDate, key: AircraftKey): string {
  const service = SERVICE_LABELS[serviceFor(nation, date)];
  return `The ${service} has begun deploying a new aircraft to its fighter units today. The new scout is called the ${aircraftLabel(key)}.`;
}
